import proposalKeys from '@/api/queries/proposal/keyFactor';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import { Deal } from '@payaca/types/dealTypes';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import JobCustomerControl from '../editJob/JobCustomerControl';

import TextareaFieldFormatter from '@payaca/components/textareaField/TextareaFieldFormatter';
import { ProposalsPermissions } from '@payaca/permissions/proposals/proposals.permissions';
import { useChangeProposal, useDeal } from '@payaca/store/hooks/appState';
import {
  requestGetChangeProposal,
  requestPersistChangeProposal,
} from '@payaca/store/proposals/proposalsActions';
import { PersistChangeProposalRequestData } from '@payaca/store/proposals/proposalsTypes';
import {
  ChangeProposal,
  ChangeProposalUploadRoles,
} from '@payaca/types/changeProposalTypes';
import AttachmentUploadsControl from '../attachmentUploadsControl/AttachmentUploadsControl';
import ChangeProposalAcceptedJobLineItemsControl from '../changeProposalAcceptedJobLineItemsControl/ChangeProposalAcceptedJobLineItemsControl';
import ChangeProposalJobLineItemsControl from '../changeProposalJobLineItemsControl/ChangeProposalJobLineItemsControl';
import EditJobSection from '../editJob/EditJobSection';
import ReferenceControl from '../referenceControl/ReferenceControl';
import ChangeProposalPaymentScheduleSection from './ChangeProposalPaymentScheduleSection';
import './EditChangeProposalControl.sass';

type Props = {
  changeProposalId: number;
};

const EditChangeProposalControl: FC<Props> = ({
  changeProposalId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSaveRequired, setIsSaveRequired] = useState(false);

  const changeProposal: ChangeProposal | undefined =
    useChangeProposal(changeProposalId);

  const deal: Deal | undefined = useDeal(changeProposal?.dealId);

  // Invalidate the CP GQL query when the CP value changes (e.g. line item
  // quantity updates, new items added) so isInSync is recomputed by the server
  const prevValueRef = useRef(changeProposal?.valueChangeIncTax);
  useEffect(() => {
    if (
      prevValueRef.current !== undefined &&
      changeProposal?.valueChangeIncTax !== prevValueRef.current
    ) {
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.changeProposal(String(changeProposalId)),
      });
    }
    prevValueRef.current = changeProposal?.valueChangeIncTax;
  }, [changeProposal?.valueChangeIncTax, changeProposalId, queryClient]);

  const [
    persistChangeProposalRequestData,
    setPersistChangeProposalRequestData,
  ] = useState<PersistChangeProposalRequestData>({
    id: changeProposal?.id,
  });

  useEffect(() => {
    if (changeProposal?.id) {
      setPersistChangeProposalRequestData({
        id: changeProposal?.id,
        notes: changeProposal?.notes,
        contactId: changeProposal?.contactId,
        customReference: changeProposal?.customReference,
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'changeProposal.customReference', 'changeProposal.contactId', and 'changeProposal.notes'
  }, [changeProposal?.id]);

  useEffect(() => {
    if (isSaveRequired) {
      setIsSaveRequired(false);
      dispatch(
        requestPersistChangeProposal(persistChangeProposalRequestData, () => {
          dispatch(requestGetChangeProposal(changeProposalId));
        })
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'persistChangeProposalRequestData', 'changeProposalId', and 'dispatch'
  }, [isSaveRequired]);

  if (!changeProposal) return null;

  return (
    <ResponsiveViewWrapper
      className="edit-change-proposal-control"
      downBreakpointSm={700}
      downBreakpointXs={450}
    >
      <ReferenceControl
        reference={changeProposal.reference}
        customReference={
          persistChangeProposalRequestData.customReference ||
          changeProposal.customReference ||
          undefined
        }
        referencedEntityName={'Change proposal'}
        onChange={(value) => {
          setPersistChangeProposalRequestData((x) => ({
            ...x,
            ...value,
          }));
          setIsSaveRequired(true);
        }}
        editPermissions={[ProposalsPermissions.EDIT_PROPOSAL_REFERENCE]}
      />
      <JobCustomerControl
        canSelectCustomer={false}
        customerId={deal?.customerId}
        jobContactId={persistChangeProposalRequestData.contactId || null}
        onSelectContact={(contactId: number) => {
          setPersistChangeProposalRequestData((x) => ({
            ...x,
            contactId: contactId,
          }));
          setIsSaveRequired(true);
        }}
      />
      <ChangeProposalAcceptedJobLineItemsControl
        changeProposalId={changeProposalId}
      />
      <ChangeProposalJobLineItemsControl changeProposalId={changeProposalId} />
      <ChangeProposalPaymentScheduleSection
        changeProposalId={String(changeProposalId)}
        futureProjectValue={
          (deal?.acceptedValue || 0) + (changeProposal?.valueChangeIncTax || 0)
        }
      />
      <EditJobSection title={'Notes'}>
        <TextareaFieldFormatter
          name="notes"
          value={persistChangeProposalRequestData.notes}
          onChange={(value) => {
            setPersistChangeProposalRequestData((x) => ({
              ...x,
              ...value,
            }));
          }}
          placeholder={`Give yourself an edge over your competitors by adding additional information to your change proposal.`}
          changeTimeoutMs={2000}
          onChangeTimeout={() => setIsSaveRequired(true)}
          onBlur={() => setIsSaveRequired(true)}
        />
      </EditJobSection>
      <AttachmentUploadsControl
        entityType="changeProposal"
        entityId={changeProposalId}
        prompt={
          'Show off some credentials. Photos of your team, completed jobs, awards, logos, industry accreditations. It all helps to win you work.'
        }
        attachmentUploadRole={ChangeProposalUploadRoles.ATTACHMENT}
        attachmentUploadIds={changeProposal.attachmentUploadIds}
        onPersistUploadSuccess={() =>
          dispatch(requestGetChangeProposal(changeProposalId))
        }
      />
    </ResponsiveViewWrapper>
  );
};
export default EditChangeProposalControl;
